import { PriorityHigh } from '@mui/icons-material'
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getDurationString, roundHours, roundUpToNearestMinute } from '../../store/utils/worklogUtils'
import { convertToDays } from '../DateUtils/convertToDays'
import BasePercentageBar, { BarSegment, LegendItem } from './BasePercentageBar'

const getHoursTooltip = (hours: number, isDisplayDaysMode: boolean): string | undefined => {
  if (isDisplayDaysMode) return undefined
  if (Number.isInteger(hours)) return undefined
  return getDurationString(hours)
}

type WorkingHoursPercentageBarProps = {
  sumAll: number
  sumWorked: number
  sumOvertime?: number
  usedOvertime?: number
  sumPaidVacation?: number
  sumUnpaidVacation?: number
  sumVacation?: number
  isDisplayDaysMode?: boolean
  sumLeft?: number
}

export const WorkingHoursPercentageBar = ({
  sumWorked,
  sumAll,
  isDisplayDaysMode = false,
  sumOvertime,
  usedOvertime,
  sumPaidVacation,
  sumUnpaidVacation,
  sumVacation = 0,
  sumLeft,
}: WorkingHoursPercentageBarProps) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const unit = isDisplayDaysMode ? 'd' : 'h'

  const { segments, legends, sumAllInYear } = useMemo(() => {
    const sumAllInYear = isDisplayDaysMode ? convertToDays(sumAll) : sumAll

    const rawWorked = roundHours(sumWorked)
    const effectiveWorked = rawWorked > 0 ? rawWorked : 0
    const sumWorkedBar = isDisplayDaysMode ? convertToDays(effectiveWorked) : effectiveWorked

    const getTotalPaidVacation = () => {
      if (sumPaidVacation != null) return sumPaidVacation
      return sumVacation
    }

    const getTotalUnpaidVacation = () => {
      if (sumUnpaidVacation != null) return sumUnpaidVacation
      return 0
    }

    const sumPaidVacationBar = isDisplayDaysMode ? convertToDays(getTotalPaidVacation()) : getTotalPaidVacation()
    const sumUnpaidVacationBar = isDisplayDaysMode ? convertToDays(getTotalUnpaidVacation()) : getTotalUnpaidVacation()
    const sumVacationBar = sumPaidVacationBar + sumUnpaidVacationBar

    const workedColor = theme.palette.primary.light
    const paidVacationColor = theme.palette.info.dark
    const unpaidVacationColor = theme.palette.info.light
    const leftoverColor = theme.palette.secondary.light

    const usedTotalBar = sumWorkedBar + sumVacationBar
    const isOverused = sumAllInYear > 0 && usedTotalBar > sumAllInYear

    const leftoverBar = parseFloat((sumAllInYear - usedTotalBar).toFixed(2))
    const leftoverClamped = leftoverBar > 0 ? leftoverBar : 0

    const usedHoursTotal = roundHours(effectiveWorked + getTotalPaidVacation() + getTotalUnpaidVacation())
    const totalHoursInYear = roundHours(sumAll)
    const leftoverHours = parseFloat((totalHoursInYear - usedHoursTotal).toFixed(2))

    let workedPercentage = 0
    let paidVacationPercentage = 0
    let unpaidVacationPercentage = 0
    let leftoverPercentage = 0

    if (sumAllInYear > 0) {
      if (isOverused) {
        if (usedTotalBar > 0) {
          workedPercentage = (sumWorkedBar / usedTotalBar) * 100
          paidVacationPercentage = (sumPaidVacationBar / usedTotalBar) * 100
          unpaidVacationPercentage = (sumUnpaidVacationBar / usedTotalBar) * 100
        }
        leftoverPercentage = 0
      } else {
        workedPercentage = (sumWorkedBar / sumAllInYear) * 100
        paidVacationPercentage = (sumPaidVacationBar / sumAllInYear) * 100
        unpaidVacationPercentage = (sumUnpaidVacationBar / sumAllInYear) * 100
        leftoverPercentage = (leftoverClamped / sumAllInYear) * 100
      }
    } else {
      if (usedTotalBar > 0) {
        workedPercentage = sumWorkedBar > 0 ? 100 : 0
      }
    }

    const showThreeSegments = sumAllInYear > 0 && sumVacationBar > 0

    const segments: BarSegment[] = []

    if (sumAllInYear <= 0) {
      const usedTotal = sumWorkedBar + sumVacationBar
      if (usedTotal > 0) {
        segments.push({
          percentage: 100,
          color: workedColor,
          value: usedTotal,
          label: t('components.percentageBar.worked'),
        })
      }
    } else if (showThreeSegments) {
      segments.push({
        percentage: workedPercentage,
        color: workedColor,
        value: sumWorkedBar,
        label: t('components.percentageBar.worked'),
      })
      if (sumPaidVacationBar > 0) {
        segments.push({
          percentage: paidVacationPercentage,
          color: paidVacationColor,
          value: sumPaidVacationBar,
          label: t('components.percentageBar.paidVacation'),
        })
      }
      if (sumUnpaidVacationBar > 0) {
        segments.push({
          percentage: unpaidVacationPercentage,
          color: unpaidVacationColor,
          value: sumUnpaidVacationBar,
          label: t('components.percentageBar.unpaidVacation'),
        })
      }
      if (leftoverPercentage > 0) {
        segments.push({
          percentage: leftoverPercentage,
          color: leftoverColor,
          value: leftoverClamped,
          label: t('components.percentageBar.left'),
        })
      }
    } else {
      const usedPct = isOverused ? 100 : workedPercentage
      const leftoverPct = Math.max(0, 100 - usedPct)

      segments.push({
        percentage: usedPct,
        color: workedColor,
        value: sumWorkedBar,
        label: t('components.percentageBar.worked'),
      })
      if (leftoverPct > 0) {
        segments.push({
          percentage: leftoverPct,
          color: leftoverColor,
          value: leftoverClamped,
          label: t('components.percentageBar.left'),
        })
      }
    }

    const legends: LegendItem[] = [
      {
        color: workedColor,
        value: `${isDisplayDaysMode ? convertToDays(effectiveWorked) : effectiveWorked}${
          isDisplayDaysMode ? 'd' : 'h'
        }`,
        label: t('components.percentageBar.worked'),
        tooltipValue: getHoursTooltip(effectiveWorked, isDisplayDaysMode),
      },
    ]

    const shouldShowLeftover = leftoverBar > 0 || leftoverHours < 0

    if (shouldShowLeftover) {
      const leftoverIndicatorColor = leftoverHours < 0 ? theme.palette.error.main : leftoverColor

      const leftoverDisplayValue = sumLeft ?? (leftoverHours < 0 ? -Math.abs(leftoverHours) : leftoverClamped)
      const leftoverLabelValue = (() => {
        if (sumLeft) {
          return `${isDisplayDaysMode ? convertToDays(sumLeft) : roundUpToNearestMinute(sumLeft)}${
            isDisplayDaysMode ? 'd' : 'h'
          }`
        }
        if (leftoverHours < 0) {
          return `${-Math.abs(
            isDisplayDaysMode ? convertToDays(leftoverHours) : roundUpToNearestMinute(leftoverHours)
          )}${isDisplayDaysMode ? 'd' : 'h'}`
        }
        return `${leftoverClamped}${isDisplayDaysMode ? 'd' : 'h'}`
      })()

      legends.push({
        color: leftoverIndicatorColor,
        value: leftoverLabelValue,
        label: t('components.percentageBar.left'),
        tooltipValue: getHoursTooltip(Math.abs(leftoverDisplayValue), isDisplayDaysMode),
      })
    }

    return { segments, legends, sumAllInYear }
  }, [sumWorked, sumAll, isDisplayDaysMode, sumPaidVacation, sumUnpaidVacation, sumVacation, sumLeft, theme.palette, t])

  const extraContent = useMemo(() => {
    const getUsedOvertime = usedOvertime || 0
    const getSumOvertime = sumOvertime || 0
    const overtimeLeft = (() => {
      const left = getSumOvertime - getUsedOvertime
      return isDisplayDaysMode ? convertToDays(left) : left
    })()

    const overtimeLeftTooltip = getHoursTooltip(overtimeLeft, isDisplayDaysMode)

    return (
      <>
        {overtimeLeft > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body1" fontWeight={700} sx={{ color: 'text.light' }}>
              {t('components.percentageBar.overtimeLeft')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title={overtimeLeftTooltip || ''}>
                <Typography
                  variant="body2"
                  fontWeight={400}
                  sx={{
                    color: 'text.light',
                    cursor: overtimeLeftTooltip ? 'help' : 'default',
                    textDecoration: overtimeLeftTooltip ? 'underline dotted' : 'none',
                  }}
                >
                  {overtimeLeft}
                  {unit}
                </Typography>
              </Tooltip>
            </Box>
          </Box>
        )}
        {overtimeLeft < 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="body1" fontWeight={700} sx={{ color: 'text.light' }}>
              {t('components.percentageBar.overtimeLeft')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip
                title={
                  <Stack alignItems={'center'}>
                    <Typography fontWeight={700}>{t('components.percentageBar.overtimeError.title')}</Typography>
                    <Typography textAlign="center" variant="caption">
                      {t('components.percentageBar.overtimeError.description')}
                    </Typography>
                    <Typography textAlign="center" variant="caption" pt={1}>
                      {t('components.percentageBar.overtimeError.overexploitation')}
                      {-overtimeLeft}h
                    </Typography>
                  </Stack>
                }
              >
                <Stack direction="row" gap={0.5} alignItems="center">
                  <Stack
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: 36,
                      bgcolor: 'error.main',
                      justifyContent: 'center',
                      alignItems: 'center',
                      transition: 'all 150ms ease-in-out',
                    }}
                  >
                    <PriorityHigh
                      sx={{
                        width: 12,
                        height: 12,
                        color: 'background.elevation9',
                        transition: 'all 150ms ease-in-out',
                      }}
                    />
                  </Stack>
                </Stack>
              </Tooltip>
            </Box>
          </Box>
        )}
      </>
    )
  }, [sumOvertime, usedOvertime, isDisplayDaysMode, unit, t])

  return (
    <BasePercentageBar
      segments={segments}
      totalValue={sumAllInYear}
      legends={legends}
      extraContent={extraContent}
      unitLabel={isDisplayDaysMode ? t('components.percentageBar.year.days') : t('components.percentageBar.year.hours')}
    />
  )
}

export default WorkingHoursPercentageBar
